import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Skeleton, Switch } from '@design-system';
import type { ThreadDetail as Thread, ThreadDraft, ThreadExchange } from '../api/api';
import { choiceInWords, reasonInWords } from '../conversationWords';
import { useReplyLibrary } from '../hooks/use-magic-email-library';
import { useConversationThread } from '../hooks/use-magic-email-conversations';
import { replyPath } from './ReplyLibrary';

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const day = (iso: string) => {
  const date = new Date(iso);
  return date.toDateString() === new Date().toDateString()
    ? time(iso)
    : date.toLocaleString([], {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      });
};

function Draft({ draft, canRead }: { draft: ThreadDraft; canRead: boolean }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 px-4 py-3.5">
      <span className="text-[13px] font-bold text-zinc-600">AI draft · not sent</span>
      {canRead ? (
        <p className="whitespace-pre-wrap text-sm leading-[1.55] text-zinc-700">{draft.text}</p>
      ) : (
        <div className="flex items-center gap-2 text-sm text-zinc-600">
          <Lock
            className="size-4"
            aria-hidden
          />
          Only reviewers can read AI drafts.
        </div>
      )}
      <span className="text-[13px] text-zinc-600">
        {draft.origin === 'parallel'
          ? 'Kept for comparison only'
          : 'Held back: no reviewed reply wrote it'}
      </span>
    </div>
  );
}

function Exchange({
  exchange,
  thread,
  titles,
}: {
  exchange: ThreadExchange;
  thread: Thread;
  titles: Map<string, string>;
}) {
  const selection = exchange.selection;
  const handedOff = selection?.outcome === 'handed_off';
  const choice = selection && !handedOff ? choiceInWords(selection) : null;

  return (
    <div className="flex flex-col gap-3">
      {exchange.inbound && (
        <>
          <span className="text-[13px] font-semibold text-zinc-600">
            Advisor wrote · {day(exchange.inbound.receivedDateTime)}
          </span>
          <div className="whitespace-pre-wrap rounded-xl bg-zinc-100 px-4 py-3.5 text-[15px] leading-[1.55]">
            {exchange.inbound.content}
          </div>
        </>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {exchange.replies.map(reply => (
          <div
            key={reply.sentDateTime}
            className={`flex flex-col gap-2 rounded-xl border bg-white px-4 py-3.5 ${handedOff ? 'border-[#F0D9AE]' : 'border-[#C9EBD4]'}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span
                className={`text-[13px] font-bold ${handedOff ? 'text-[#8A4B00]' : 'text-[#1B6B3A]'}`}
              >
                {handedOff ? 'Sent to our team' : 'Sent'} · {day(reply.sentDateTime)}
              </span>
              {handedOff && thread.handoff?.intercomUrl ? (
                <a
                  href={thread.handoff.intercomUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px]"
                >
                  Open in Intercom
                </a>
              ) : selection?.templateId ? (
                <span className="text-[13px] text-zinc-600">
                  Reply:{' '}
                  <Link to={replyPath(selection.templateId)}>
                    {titles.get(selection.templateId) ?? selection.templateId}
                  </Link>
                </span>
              ) : null}
            </div>
            <p className="whitespace-pre-wrap text-sm leading-[1.55]">{reply.content}</p>
            {handedOff ? (
              <span className="text-[13px] text-zinc-600">
                Why: {reasonInWords(selection.reason)}
              </span>
            ) : choice ? (
              <span className="text-[13px] text-zinc-600">{choice}</span>
            ) : !selection ? (
              <span className="text-[13px] text-zinc-600">
                {reply.model === 'template'
                  ? 'A fixed message'
                  : 'Answered before the reply library'}
              </span>
            ) : null}
          </div>
        ))}
        {exchange.drafts.map(draft => (
          <Draft
            key={`${draft.origin}-${draft.createdDateTime}`}
            draft={draft}
            canRead={thread.canReviewDrafts}
          />
        ))}
      </div>
    </div>
  );
}

/** One conversation: every email in it, what went back, the AI's draft beside it, and the switch. */
export function ThreadDetail({
  advisorId,
  threadKey,
  live,
}: {
  advisorId: string;
  threadKey: string;
  live: boolean;
}) {
  const view = useConversationThread(advisorId, threadKey, live);
  const library = useReplyLibrary();
  const titles = new Map((library.data?.entries ?? []).map(entry => [entry.entryId, entry.title]));

  if (view.query.isPending) return <Skeleton className="h-96 w-full" />;
  if (view.query.isError)
    return (
      <p className="text-sm text-red-700">
        Could not load the conversation: {view.query.error.message}
      </p>
    );
  const thread = view.thread!;
  const first = thread.exchanges.find(exchange => exchange.inbound)?.inbound;
  const emails = thread.exchanges.filter(exchange => exchange.inbound).length;

  return (
    <section className="flex min-w-0 flex-col gap-5 rounded-xl border border-zinc-200 bg-white px-6 pb-6 pt-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="line-clamp-2 text-xl font-semibold">
            {first?.content.split('\n')[0] ?? 'Conversation'}
          </h2>
          <span className="text-sm text-zinc-600">
            {thread.advisorEmail ?? thread.advisorId} · {emails} {emails === 1 ? 'email' : 'emails'}
            {first && ` · started ${day(first.receivedDateTime)}`}
          </span>
        </div>
        <label className="flex items-center gap-2.5 text-sm font-medium text-zinc-700">
          Assistant answers this conversation
          <Switch
            checked={!thread.assistant.off}
            disabled={view.switching}
            onCheckedChange={view.setAssistantOn}
          />
        </label>
      </div>
      <p className="-mt-2.5 text-[13px] text-zinc-600">
        {thread.assistant.off
          ? 'Off: a person answers the next email in Intercom. Turn it back on to let the assistant answer again.'
          : 'Turn it off to take over: the next email goes to a person in Intercom.'}
      </p>
      {thread.exchanges.map((exchange, index) => (
        <div
          key={exchange.inbound?.messageId ?? `exchange-${index}`}
          className="flex flex-col gap-5"
        >
          {index > 0 && <div className="h-px bg-zinc-200" />}
          <Exchange
            exchange={exchange}
            thread={thread}
            titles={titles}
          />
        </div>
      ))}
    </section>
  );
}
